import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { sucursalesService } from '../../services/sucursalesService';
import { empresasService } from '../../services/empresasService';

function SucursalForm({ sucursal, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    empresaID: '',
    nombre: '',
    direccion: '',
    telefono: '',
    ciudad: '',
    codigoPostal: '',
    zona: '',
    activo: true,
  });

  const [empresas, setEmpresas] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (sucursal) {
      setFormData({
        empresaID: sucursal.EmpresaID || sucursal.empresaID || '',
        nombre: sucursal.Nombre || sucursal.nombre || '',
        direccion: sucursal.Direccion || sucursal.direccion || '',
        telefono: sucursal.Telefono || sucursal.telefono || '',
        ciudad: sucursal.Ciudad || sucursal.ciudad || '',
        codigoPostal: sucursal.CodigoPostal || sucursal.codigoPostal || '',
        zona: sucursal.Zona || sucursal.zona || '',
        activo: sucursal.Activo !== undefined ? sucursal.Activo : (sucursal.activo !== undefined ? sucursal.activo : true),
      });
    }
  }, [sucursal]);

  const loadEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const normalized = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
        }));
        setEmpresas(normalized);

        // Auto-seleccionar si solo hay 1 empresa y no se está editando ni hay un valor asignado
        if (normalized.length === 1 && (!formData.empresaID || formData.empresaID === '')) {
          setFormData(prev => ({ ...prev, empresaID: normalized[0].EmpresaID }));
        }
      }
    } catch (err) {
      setError('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // EmpresaID es requerido
    if (!formData.empresaID) {
      newErrors.empresaID = 'La empresa es requerida';
    }

    // Nombre es requerido
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      newErrors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    // Direccion opcional pero con límite
    if (formData.direccion && formData.direccion.length > 500) {
      newErrors.direccion = 'La dirección no puede exceder 500 caracteres';
    }

    // Telefono opcional pero con límite
    if (formData.telefono && formData.telefono.length > 20) {
      newErrors.telefono = 'El teléfono no puede exceder 20 caracteres';
    }

    // Ciudad opcional pero con límite
    if (formData.ciudad && formData.ciudad.length > 100) {
      newErrors.ciudad = 'La ciudad no puede exceder 100 caracteres';
    }

    // CodigoPostal opcional pero debe ser válido (5 dígitos)
    if (formData.codigoPostal) {
      if (!/^\d{5}$/.test(formData.codigoPostal.trim())) {
        newErrors.codigoPostal = 'El código postal debe tener 5 dígitos';
      } else if (formData.codigoPostal.length > 10) {
        newErrors.codigoPostal = 'El código postal no puede exceder 10 caracteres';
      }
    }

    // Zona opcional pero con límite
    if (formData.zona && formData.zona.length > 100) {
      newErrors.zona = 'La zona no puede exceder 100 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const sucursalData = {
        EmpresaID: parseInt(formData.empresaID),
        Nombre: formData.nombre.trim(),
        Direccion: formData.direccion?.trim() || null,
        Telefono: formData.telefono?.trim() || null,
        Ciudad: formData.ciudad?.trim() || null,
        CodigoPostal: formData.codigoPostal?.trim() || null,
        Zona: formData.zona?.trim() || null,
      };

      let response;
      if (sucursal) {
        // Actualizar
        const sucursalId = sucursal.SucursalID || sucursal.sucursalID;
        sucursalData.SucursalID = sucursalId;
        sucursalData.Activo = formData.activo;
        response = await sucursalesService.update(sucursalId, sucursalData);
      } else {
        // Crear
        response = await sucursalesService.create(sucursalData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al guardar la sucursal');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Empresa <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="empresaID"
              value={formData.empresaID}
              onChange={handleChange}
              invalid={!!errors.empresaID}
              disabled={loading || loadingEmpresas || empresas.length <= 1}
              required
            >
              <option value="">Seleccione una empresa</option>
              {empresas.map((emp) => (
                <option key={emp.EmpresaID} value={emp.EmpresaID}>
                  {emp.RazonSocial}
                </option>
              ))}
            </Input>
            {errors.empresaID && (
              <div className="text-danger small">{errors.empresaID}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>
              Nombre <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              invalid={!!errors.nombre}
              maxLength={200}
              required
            />
            {errors.nombre && (
              <div className="text-danger small">{errors.nombre}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Dirección</Label>
            <Input
              type="textarea"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
              invalid={!!errors.direccion}
              maxLength={500}
              rows={3}
            />
            {errors.direccion && (
              <div className="text-danger small">{errors.direccion}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Teléfono</Label>
            <Input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              invalid={!!errors.telefono}
              maxLength={20}
            />
            {errors.telefono && (
              <div className="text-danger small">{errors.telefono}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Ciudad</Label>
            <Input
              type="text"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
              invalid={!!errors.ciudad}
              maxLength={100}
            />
            {errors.ciudad && (
              <div className="text-danger small">{errors.ciudad}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Código Postal</Label>
            <Input
              type="text"
              name="codigoPostal"
              value={formData.codigoPostal}
              onChange={handleChange}
              invalid={!!errors.codigoPostal}
              maxLength={10}
              placeholder="Ej: 01000"
              pattern="[0-9]{5}"
            />
            {errors.codigoPostal && (
              <div className="text-danger small">{errors.codigoPostal}</div>
            )}
            <small className="form-text text-muted">
              Código postal de 5 dígitos (requerido para facturación)
            </small>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Zona</Label>
            <Input
              type="text"
              name="zona"
              value={formData.zona}
              onChange={handleChange}
              invalid={!!errors.zona}
              maxLength={100}
            />
            {errors.zona && (
              <div className="text-danger small">{errors.zona}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      {sucursal && (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
            Sucursal Activa
          </Label>
        </FormGroup>
      )}

      <div className="text-right mt-4">
        <Button color="secondary" onClick={onCancel} className="mr-2">
          Cancelar
        </Button>
        <Button color="primary" type="submit" disabled={loading || loadingEmpresas}>
          {loading ? 'Guardando...' : sucursal ? 'Actualizar' : 'Crear'}
        </Button>
      </div>
    </Form>
  );
}

export default SucursalForm;

